import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '@/api';
import { useOrgStore } from '@/store/orgStore';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Plus, Search, MoreHorizontal, Pencil, Trash2, Download } from 'lucide-react';
import FileAttachments from '@/components/FileAttachments';

const stageColors: Record<string, string> = {
  'Prospecto': 'bg-[#F1F5F9] text-[#475569] border-[#94A3B8]/30',
  'Contacto realizado': 'bg-[#EFF6FF] text-[#2563EB] border-[#2563EB]/20',
  'Reunion / demo': 'bg-[#ECFEFF] text-[#0F766E] border-[#14B8A6]/20',
  'Propuesta enviada': 'bg-[#FFFBEB] text-[#B45309] border-[#F59E0B]/20',
  'Negociacion': 'bg-[#F5F3FF] text-[#7C3AED] border-[#8B5CF6]/20',
  'Cerrado ganado': 'bg-[#DCFCE7] text-[#15803D] border-[#22C55E]/20',
  'Cerrado perdido': 'bg-[#FEE2E2] text-[#B91C1C] border-[#EF4444]/20',
};

const healthColors: Record<string, string> = {
  saludable: 'bg-[#DCFCE7] text-[#15803D] border-[#22C55E]/20',
  riesgo: 'bg-[#FFFBEB] text-[#B45309] border-[#F59E0B]/20',
  alto_riesgo: 'bg-[#FEE2E2] text-[#B91C1C] border-[#EF4444]/20',
};

const formatCLP = (n: number) => new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', minimumFractionDigits: 0 }).format(n);
const emptyOpp = {
  name: '',
  monto_neto: 0,
  iva: 0,
  total_bruto: 0,
  exento: false,
  amount: 0,
  stage: 'Prospecto',
  probability: 15,
  close_date: '',
  account_id: '',
  contact_id: '',
  assigned_to: '',
  next_step: '',
  next_step_date: '',
  lost_reason: '',
  qualification_source: '',
  manual_mode: false,
  manual_mode_reason: '',
};

export default function OpportunitiesPage() {
  const [searchParams] = useSearchParams();
  const [opps, setOpps] = useState<any[]>([]);
  const [companies, setCompanies] = useState<any[]>([]);
  const [contacts, setContacts] = useState<any[]>([]);
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [editItem, setEditItem] = useState<any>(null);
  const [form, setForm] = useState(emptyOpp);
  const [saving, setSaving] = useState(false);
  const { currentOrgId } = useOrgStore();

  const fetchData = useCallback(async () => {
    if (!currentOrgId) return;
    const params: any = {};
    if (search) params.search = search;
    if (stageFilter) params.stage = stageFilter;
    const [oppsRes, compsRes, contsRes] = await Promise.all([
      api.get('/opportunities', { params }),
      api.get('/companies'),
      api.get('/contacts'),
    ]);
    setOpps(oppsRes.data);
    setCompanies(compsRes.data);
    setContacts(contsRes.data);
  }, [currentOrgId, search, stageFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => {
    const highlight = searchParams.get('highlight');
    if (!highlight) return;
    const el = document.getElementById(`opp-row-${highlight}`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [searchParams, opps]);

  const getCompanyName = (id: string) => companies.find(c => c.id === id)?.name || '';
  const getContactName = (id: string) => contacts.find(c => c.id === id)?.name || '';
  const filteredContacts = form.account_id ? contacts.filter(c => c.company_id === form.account_id) : contacts;

  const openCreate = () => {
    setEditItem(null);
    setForm({
      ...emptyOpp,
      next_step: 'Definir siguiente contacto',
      next_step_date: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
    });
    setDialogOpen(true);
  };
  const openEdit = (item: any) => {
    setEditItem(item);
    setForm({
      name: item.name,
      monto_neto: item.monto_neto || item.amount || 0,
      iva: item.iva || 0,
      total_bruto: item.total_bruto || item.amount || 0,
      exento: item.exento || false,
      amount: item.amount || 0,
      stage: item.stage,
      probability: item.probability,
      close_date: item.close_date,
      account_id: item.account_id,
      contact_id: item.contact_id,
      assigned_to: item.assigned_to,
      next_step: item.next_step || '',
      next_step_date: item.next_step_date || '',
      lost_reason: item.lost_reason || '',
      qualification_source: item.qualification_source || '',
      manual_mode: Boolean(item.manual_mode),
      manual_mode_reason: item.manual_mode_reason || '',
    });
    setDialogOpen(true);
  };

  const updateMontoNeto = (neto: number, exento: boolean) => {
    const ivaCalc = exento ? 0 : Math.round(neto * 0.19);
    const bruto = neto + ivaCalc;
    setForm(prev => ({ ...prev, monto_neto: neto, iva: ivaCalc, total_bruto: bruto, amount: bruto, exento }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        qualification_source: form.manual_mode ? 'manual-qualified' : form.qualification_source,
        manual_mode: Boolean(form.manual_mode),
        manual_mode_reason: form.manual_mode ? form.manual_mode_reason : '',
      };
      if (editItem) {
        await api.put(`/opportunities/${editItem.id}`, payload);
        toast.success('Oportunidad actualizada');
      }
      else {
        await api.post('/opportunities', payload);
        toast.success('Oportunidad creada');
      }
      setDialogOpen(false); fetchData();
    } catch (error: any) { toast.error(error?.response?.data?.detail || 'Error al guardar'); }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try { await api.delete(`/opportunities/${deleteId}`); toast.success('Oportunidad eliminada'); setDeleteId(null); fetchData(); }
    catch { toast.error('Error al eliminar'); }
  };

  const handleExportCSV = async () => {
    try {
      const response = await api.get('/opportunities/export/csv', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a'); link.href = url; link.setAttribute('download', 'oportunidades.csv'); document.body.appendChild(link); link.click(); link.remove();
      toast.success('CSV exportado');
    } catch { toast.error('Error al exportar'); }
  };

  return (
    <div data-testid="opportunities-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#0F172A]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Oportunidades</h1>
          <p className="text-sm text-[#475569] mt-1">{opps.length} oportunidades registradas</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto">
          <Button variant="outline" onClick={handleExportCSV} className="flex-1 sm:flex-none min-h-11 border-[#E2E8F0] text-[#475569] font-medium" data-testid="export-opps-csv"><Download size={16} className="mr-2" /> Exportar</Button>
          <Button onClick={openCreate} className="flex-1 sm:flex-none min-h-11 bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-semibold" data-testid="create-opp-btn">
            <Plus size={16} className="mr-2" /> Nueva Oportunidad
          </Button>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
          <Input placeholder="Buscar oportunidades..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 border-[#E2E8F0]" data-testid="opps-search" />
        </div>
        <Select value={stageFilter} onValueChange={(v) => setStageFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-44 border-[#E2E8F0]" data-testid="opps-stage-filter"><SelectValue placeholder="Etapa" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas</SelectItem>
            {Object.keys(stageColors).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="hidden lg:block bg-white border border-[#E2E8F0] rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left" data-testid="opps-table">
            <thead><tr className="border-b border-[#E2E8F0] bg-[#F8FAFC]">
              <th className="py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#64748B]">Nombre</th>
              <th className="py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#64748B] hidden xl:table-cell">Empresa</th>
              <th className="py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#64748B]">Neto</th>
              <th className="py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#64748B] hidden xl:table-cell">IVA</th>
              <th className="py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#64748B]">Total Bruto</th>
              <th className="py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#64748B]">Etapa</th>
              <th className="py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#64748B]">Salud</th>
              <th className="py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#64748B] hidden 2xl:table-cell">Cierre</th>
              <th className="py-3 px-4 w-12"></th>
            </tr></thead>
            <tbody>
              {opps.map((o) => (
                <tr key={o.id} id={`opp-row-${o.id}`} className="border-b border-[#E2E8F0] hover:bg-[#F1F5F9] transition-colors">
                  <td className="py-3 px-4 font-medium text-[#0F172A]">
                    <Link to={`/oportunidades/${o.id}`} className="hover:text-[#2563EB]">{o.name}</Link>
                  </td>
                  <td className="py-3 px-4 text-[#475569] text-xs hidden xl:table-cell">{getCompanyName(o.account_id) || '-'}</td>
                  <td className="py-3 px-4 text-[#475569] text-xs tabular-nums">{formatCLP(o.monto_neto || o.amount || 0)}</td>
                  <td className="py-3 px-4 text-[#475569] text-xs tabular-nums hidden xl:table-cell">{o.exento ? <Badge variant="outline" className="text-[10px] bg-[#F1F5F9] text-[#475569]">Exento</Badge> : formatCLP(o.iva || 0)}</td>
                  <td className="py-3 px-4 text-[#0F172A] font-semibold text-xs tabular-nums">{formatCLP(o.total_bruto || o.amount || 0)}</td>
                  <td className="py-3 px-4"><Badge variant="outline" className={`text-xs font-medium ${stageColors[o.stage] || ''}`}>{o.stage}</Badge></td>
                  <td className="py-3 px-4">
                    <Badge
                      variant="outline"
                      className={`text-xs font-semibold ${healthColors[o.health_band] || 'bg-[#F1F5F9] text-[#475569] border-[#E2E8F0]'}`}
                      title={[
                        `Salud: ${typeof o.health_score === 'number' ? o.health_score : '-'}`,
                        o.health_recommendation ? `Recomendación: ${o.health_recommendation}` : '',
                        Array.isArray(o.health_reasons) ? o.health_reasons.slice(0, 5).join(' | ') : '',
                      ].filter(Boolean).join(' • ')}
                    >
                      {typeof o.health_score === 'number' ? o.health_score : '-'}
                    </Badge>
                  </td>
                  <td className="py-3 px-4 text-[#475569] hidden 2xl:table-cell">{o.close_date}</td>
                  <td className="py-3 px-4">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8"><MoreHorizontal size={16} /></Button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEdit(o)}><Pencil size={14} className="mr-2" /> Editar</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setDeleteId(o.id)} className="text-[#EF4444]"><Trash2 size={14} className="mr-2" /> Eliminar</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
              {opps.length === 0 && <tr><td colSpan={6} className="py-12 text-center text-[#94A3B8]">No hay oportunidades</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <ul className="lg:hidden space-y-3" data-testid="opps-card-list">
        {opps.map((o) => (
          <li key={o.id} id={`opp-row-${o.id}`} className="rounded-lg border border-[#E2E8F0] bg-white p-4 shadow-sm">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <Link to={`/oportunidades/${o.id}`} className="font-semibold text-[#0F172A] hover:text-[#2563EB]">{o.name}</Link>
                <p className="text-xs text-[#475569] mt-0.5 truncate">{getCompanyName(o.account_id) || 'Sin empresa'}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Badge variant="outline" className={`text-xs font-medium ${stageColors[o.stage] || ''}`}>{o.stage}</Badge>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-11 w-11"><MoreHorizontal size={16} /></Button></DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => openEdit(o)}><Pencil size={14} className="mr-2" /> Editar</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setDeleteId(o.id)} className="text-[#EF4444]"><Trash2 size={14} className="mr-2" /> Eliminar</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
            <dl className="mt-3 grid grid-cols-2 gap-y-1.5 gap-x-3 text-sm">
              <dt className="text-[#64748B]">Neto</dt>
              <dd className="text-right text-[#475569] tabular-nums">{formatCLP(o.monto_neto || o.amount || 0)}</dd>
              <dt className="text-[#64748B]">IVA</dt>
              <dd className="text-right text-[#475569] tabular-nums">{o.exento ? 'Exento' : formatCLP(o.iva || 0)}</dd>
              <dt className="text-[#64748B]">Total Bruto</dt>
              <dd className="text-right text-[#0F172A] font-semibold tabular-nums">{formatCLP(o.total_bruto || o.amount || 0)}</dd>
              <dt className="text-[#64748B]">Salud</dt>
              <dd className="text-right">
                <Badge
                  variant="outline"
                  className={`text-xs font-semibold ${healthColors[o.health_band] || 'bg-[#F1F5F9] text-[#475569] border-[#E2E8F0]'}`}
                  title={[
                    `Salud: ${typeof o.health_score === 'number' ? o.health_score : '-'}`,
                    o.health_recommendation ? `Recomendación: ${o.health_recommendation}` : '',
                    Array.isArray(o.health_reasons) ? o.health_reasons.slice(0, 5).join(' | ') : '',
                  ].filter(Boolean).join(' • ')}
                >
                  {typeof o.health_score === 'number' ? o.health_score : '-'}
                </Badge>
              </dd>
              <dt className="text-[#64748B]">Cierre</dt>
              <dd className="text-right text-[#475569] tabular-nums">{o.close_date || '-'}</dd>
            </dl>
          </li>
        ))}
        {opps.length === 0 && (
          <li className="rounded-lg border border-dashed border-[#E2E8F0] bg-[#F8FAFC] p-8 text-center text-sm text-[#94A3B8]">
            No hay oportunidades
          </li>
        )}
      </ul>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="opp-dialog">
          <DialogHeader><DialogTitle className="font-bold text-[#0F172A]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{editItem ? 'Editar Oportunidad' : 'Nueva Oportunidad'}</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            {!editItem && (
              <div className="rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] p-4">
                <p className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">Origen comercial</p>
                <p className="mt-2 text-sm text-[#475569]">
                  El flujo recomendado es crear la oportunidad desde la conversión de un lead en <Link to="/leads" className="font-medium text-[#2563EB] hover:underline">Leads</Link>.
                  El alta manual queda disponible solo para QA y pruebas controladas.
                </p>
                <label className="mt-3 flex items-center gap-2 text-sm font-medium text-[#0F172A]">
                  <input
                    type="checkbox"
                    checked={Boolean(form.manual_mode)}
                    onChange={(e) => setForm((prev) => ({
                      ...prev,
                      manual_mode: e.target.checked,
                      qualification_source: e.target.checked ? 'manual-qualified' : '',
                      manual_mode_reason: e.target.checked ? prev.manual_mode_reason : '',
                    }))}
                    className="h-4 w-4 rounded border-[#CBD5E1] text-[#2563EB] focus:ring-[#2563EB]"
                  />
                  Crear manualmente solo para QA/pruebas
                </label>
                {form.manual_mode && (
                  <div className="mt-3">
                    <Label className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">Motivo QA</Label>
                    <Input
                      value={form.manual_mode_reason || ''}
                      onChange={(e) => setForm((prev) => ({ ...prev, manual_mode_reason: e.target.value }))}
                      className="mt-1 border-[#E2E8F0]"
                      placeholder="Ej: smoke test pipeline, seed manual, QA funcional"
                    />
                  </div>
                )}
              </div>
            )}
            <div><Label className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">Nombre</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="border-[#E2E8F0]" data-testid="opp-name-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">Empresa</Label>
                <Select value={form.account_id || "none"} onValueChange={(v) => setForm({ ...form, account_id: v === "none" ? "" : v, contact_id: "" })}>
                  <SelectTrigger className="border-[#E2E8F0]" data-testid="opp-company-select"><SelectValue placeholder="Seleccionar empresa" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin empresa</SelectItem>
                    {companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select></div>
              <div><Label className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">Contacto</Label>
                <Select value={form.contact_id || "none"} onValueChange={(v) => setForm({ ...form, contact_id: v === "none" ? "" : v })}>
                  <SelectTrigger className="border-[#E2E8F0]" data-testid="opp-contact-select"><SelectValue placeholder={form.account_id ? "Seleccionar" : "Elija empresa"} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin contacto</SelectItem>
                    {filteredContacts.map(c => <SelectItem key={c.id} value={c.id}>{c.name}{c.position ? ` (${c.position})` : ''}</SelectItem>)}
                  </SelectContent>
                </Select></div>
            </div>
            <div className="p-3 bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">Calculo Financiero</span>
                <label className="flex items-center gap-2 cursor-pointer">
                  <span className="text-xs text-[#475569]">Exento IVA</span>
                  <button type="button" onClick={() => updateMontoNeto(form.monto_neto, !form.exento)} className={`relative w-10 h-5 rounded-full transition-colors ${form.exento ? 'bg-[#2563EB]' : 'bg-[#CBD5E1]'}`} data-testid="opp-exento-switch">
                    <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${form.exento ? 'translate-x-5' : 'translate-x-0.5'}`} />
                  </button>
                </label>
              </div>
              <div><Label className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">Monto Neto (CLP)</Label>
                <Input type="number" value={form.monto_neto || ''} onChange={(e) => updateMontoNeto(Number(e.target.value) || 0, form.exento)} className="border-[#E2E8F0] font-mono" data-testid="opp-monto-neto" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">IVA (19%)</Label>
                  <Input value={formatCLP(form.iva || 0)} readOnly className="border-[#E2E8F0] bg-[#F1F5F9] font-mono text-[#475569]" /></div>
                <div><Label className="text-xs font-bold uppercase tracking-widest text-[#2563EB]">Total Bruto</Label>
                  <Input value={formatCLP(form.total_bruto || 0)} readOnly className="border-[#2563EB]/20 bg-[#EFF6FF] font-mono font-bold text-[#0F172A]" /></div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">Etapa</Label>
                <Select value={form.stage} onValueChange={(v) => setForm({ ...form, stage: v })}>
                  <SelectTrigger className="border-[#E2E8F0]" data-testid="opp-stage-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.keys(stageColors).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">Probabilidad %</Label>
                <Input type="number" min={0} max={100} value={form.probability} onChange={(e) => setForm({ ...form, probability: Number(e.target.value) })} className="border-[#E2E8F0]" /></div>
            </div>
            <div><Label className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">Fecha de Cierre</Label>
              <Input type="date" value={form.close_date} onChange={(e) => setForm({ ...form, close_date: e.target.value })} className="border-[#E2E8F0]" /></div>
            <div><Label className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">Próximo Paso</Label>
              <Input value={form.next_step || ''} onChange={(e) => setForm({ ...form, next_step: e.target.value })} className="border-[#E2E8F0]" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">Fecha Próximo Paso</Label>
                <Input type="date" value={form.next_step_date || ''} onChange={(e) => setForm({ ...form, next_step_date: e.target.value })} className="border-[#E2E8F0]" /></div>
              <div><Label className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">Motivo perdido</Label>
                <Input value={form.lost_reason || ''} onChange={(e) => setForm({ ...form, lost_reason: e.target.value })} className="border-[#E2E8F0]" placeholder="Solo si aplica" /></div>
            </div>
          </div>
          {editItem && <FileAttachments entityType="opportunity" entityId={editItem.id} />}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-[#E2E8F0]">Cancelar</Button>
            <Button
              onClick={handleSave}
              disabled={saving || !form.name || (!editItem && !form.manual_mode)}
              className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
              data-testid="opp-save-btn"
            >
              {saving ? 'Guardando...' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Eliminar Oportunidad</AlertDialogTitle>
          <AlertDialogDescription>Esta accion no se puede deshacer.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-[#EF4444] hover:bg-[#DC2626] text-white">Eliminar</AlertDialogAction>
          </AlertDialogFooter></AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
